import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import { pricingPlans, pricingAddOns, pricingFaqs } from '@/mocks/pricingData';

export default function PricingPage() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState<'yearly' | 'monthly'>('yearly');
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-firmai-dark" ref={sectionRef}>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-firmai-darker to-firmai-dark" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-firmai-green/8 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-firmai-blue/8 rounded-full blur-3xl" />

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">定价方案</span>
              <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl text-white mt-4 leading-tight">
                选择适合您的
                <br />
                <span className="bg-gradient-to-r from-firmai-green to-firmai-blue bg-clip-text text-transparent">
                  能源管理方案
                </span>
              </h1>
              <p className="text-firmai-text mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
                从中小企业到大型集团，Firmai 提供灵活的定价方案，满足不同规模企业的能源数字化需求
              </p>
            </div>

            {/* Billing Toggle */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 inline-flex items-center gap-3 mt-10 p-1.5 bg-firmai-card border border-firmai-border rounded-xl">
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  billingCycle === 'yearly'
                    ? 'bg-firmai-green text-firmai-dark'
                    : 'text-firmai-text hover:text-white'
                }`}
              >
                年付 <span className="text-xs opacity-70">(省 20%)</span>
              </button>
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  billingCycle === 'monthly'
                    ? 'bg-firmai-green text-firmai-dark'
                    : 'text-firmai-text hover:text-white'
                }`}
              >
                月付
              </button>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="relative py-8 md:py-12 bg-firmai-dark">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {pricingPlans.map((plan, index) => (
                <div
                  key={plan.id}
                  className={`reveal opacity-0 translate-y-6 transition-all duration-700 relative rounded-xl overflow-hidden ${
                    plan.popular
                      ? 'bg-firmai-card border-2 border-firmai-green/40 shadow-lg shadow-firmai-green/5'
                      : 'bg-firmai-card border border-firmai-border hover:border-firmai-green/20'
                  } transition-all duration-300`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-firmai-green text-firmai-dark text-xs font-semibold text-center py-1.5">
                      最受欢迎
                    </div>
                  )}

                  <div className={`p-6 md:p-8 ${plan.popular ? 'pt-10' : ''}`}>
                    {/* Plan Header */}
                    <div className="mb-6">
                      <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
                      <p className="text-firmai-muted text-sm mt-1">{plan.subtitle}</p>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        {plan.price !== '定制' && <span className="text-firmai-muted text-lg">¥</span>}
                        <span className="text-white font-display font-bold text-3xl md:text-4xl">
                          {plan.price === '定制' ? '定制' : billingCycle === 'yearly' ? plan.price : (parseInt(plan.price.replace(/,/g, '')) / 10).toLocaleString()}
                        </span>
                        <span className="text-firmai-muted text-sm">{plan.period}</span>
                      </div>
                      <p className="text-firmai-text text-sm mt-2">{plan.description}</p>
                    </div>

                    {/* CTA */}
                    <button
                      onClick={() => {
                        if (plan.id === 'enterprise') {
                          navigate('/');
                          setTimeout(() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' }), 100);
                        } else {
                          navigate('/');
                          setTimeout(() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' }), 100);
                        }
                      }}
                      className={`w-full py-3 rounded-md font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95 whitespace-nowrap ${
                        plan.ctaStyle === 'primary'
                          ? 'bg-firmai-green text-firmai-dark hover:bg-firmai-green/90'
                          : 'border border-firmai-border text-white hover:border-firmai-green hover:text-firmai-green'
                      }`}
                    >
                      {plan.cta}
                    </button>

                    {/* Features */}
                    <div className="mt-6 pt-6 border-t border-firmai-border space-y-3">
                      {plan.features.map((feature) => (
                        <div key={feature.text} className="flex items-start gap-3">
                          <span className={`w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 ${
                            feature.included ? 'text-firmai-green' : 'text-firmai-muted/40'
                          }`}>
                            <i className={feature.included ? 'ri-check-line' : 'ri-close-line'} />
                          </span>
                          <span className={`text-sm ${feature.included ? 'text-firmai-text' : 'text-firmai-muted/60'}`}>
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="relative py-16 md:py-20 bg-firmai-darker">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10">
              <h2 className="font-display font-bold text-xl md:text-2xl text-white">增值服务</h2>
              <p className="text-firmai-text mt-2 text-sm md:text-base">根据您的需求灵活搭配</p>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pricingAddOns.map((addon, index) => (
                <div
                  key={addon.name}
                  className="bg-firmai-card border border-firmai-border rounded-xl p-5 hover:border-firmai-green/20 transition-all duration-300"
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium text-sm">{addon.name}</h4>
                    <span className="text-firmai-green font-semibold text-sm">{addon.price}</span>
                  </div>
                  <p className="text-firmai-muted text-xs">{addon.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="relative py-16 md:py-20 bg-firmai-dark">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10">
              <h2 className="font-display font-bold text-xl md:text-2xl text-white">功能对比</h2>
              <p className="text-firmai-text mt-2 text-sm md:text-base">详细了解各版本功能差异</p>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 max-w-4xl mx-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-firmai-border">
                    <th className="text-left text-firmai-muted font-medium py-4 px-4">功能</th>
                    <th className="text-center text-firmai-muted font-medium py-4 px-4">启航版</th>
                    <th className="text-center text-firmai-green font-medium py-4 px-4">专业版</th>
                    <th className="text-center text-firmai-muted font-medium py-4 px-4">企业版</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: '设备接入数量', starter: '50台', pro: '200台', enterprise: '无限' },
                    { feature: '实时监测仪表盘', starter: '✓', pro: '✓', enterprise: '✓' },
                    { feature: 'AI 智能分析', starter: '—', pro: '✓', enterprise: '✓' },
                    { feature: '预测性维护', starter: '—', pro: '基础版', enterprise: '高级版' },
                    { feature: '碳排放追踪', starter: '—', pro: '✓', enterprise: '全链路' },
                    { feature: '企业工具集成', starter: '—', pro: '钉钉/飞书', enterprise: '全平台+API' },
                    { feature: '绿色工厂申报', starter: '—', pro: '—', enterprise: '✓' },
                    { feature: 'CCER 项目开发', starter: '—', pro: '—', enterprise: '✓' },
                    { feature: 'CBAM 合规支持', starter: '—', pro: '—', enterprise: '✓' },
                    { feature: '私有化部署', starter: '—', pro: '—', enterprise: '✓' },
                    { feature: '专属客户成功经理', starter: '—', pro: '✓', enterprise: '✓' },
                    { feature: '7x24 技术支持', starter: '在线客服', pro: '专属支持', enterprise: '专属+现场' },
                  ].map((row, idx) => (
                    <tr key={row.feature} className={`border-b border-firmai-border/50 ${idx % 2 === 0 ? 'bg-firmai-dark/30' : ''}`}>
                      <td className="text-firmai-text py-3 px-4">{row.feature}</td>
                      <td className="text-center text-firmai-muted py-3 px-4">{row.starter}</td>
                      <td className="text-center text-firmai-green py-3 px-4 font-medium">{row.pro}</td>
                      <td className="text-center text-firmai-muted py-3 px-4">{row.enterprise}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-16 md:py-20 bg-firmai-darker">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10">
              <h2 className="font-display font-bold text-xl md:text-2xl text-white">常见问题</h2>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 max-w-2xl mx-auto space-y-3">
              {pricingFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-firmai-card border border-firmai-border rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors duration-200"
                  >
                    <span className="text-white text-sm font-medium pr-4">{faq.question}</span>
                    <span className={`w-5 h-5 flex items-center justify-center text-firmai-green shrink-0 transition-transform duration-300 ${
                      expandedFaq === index ? 'rotate-180' : ''
                    }`}>
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5 animate-expand-in">
                      <p className="text-firmai-text text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.06)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">还有疑问？</h2>
              <p className="text-firmai-text mt-4 text-base md:text-lg">
                我们的专家团队随时为您解答，帮助您选择最适合的方案
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                <a
                  href="tel:18916035855"
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-firmai-green hover:text-firmai-green transition-all duration-200 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line" />
                  </span>
                  18916035855
                </a>
                <a
                  href="/#demo"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                    setTimeout(() => {
                      document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  预约演示
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes expand-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-expand-in {
          animation: expand-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}